import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Row, Table } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../Context/globalContext";
import FirebaseApp from "../firebase";
import { onValue, ref } from "firebase/database";
import moment from "moment";
import { BsArrowLeft } from "react-icons/bs";

const parseTimestampValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
        return numeric;
    }
    const parsed = moment(value).valueOf();
    return Number.isNaN(parsed) ? null : parsed;
};

const formatTimestampDisplay = (value) => {
    const parsed = parseTimestampValue(value);
    return parsed === null ? "-" : moment(parsed).format("DD-MM-YYYY hh:mm A");
};

const stringifyValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch (err) {
        return String(value);
    }
};

const escapeCsvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const DeviceSessions = () => {

    const { setLoader } = useContext(GlobalContext);
    const navigate = useNavigate();
    const { device_id, company_key } = useParams();
    const [sessionRecords, setSessionRecords] = useState([]);
    const [onboardingRecords, setOnboardingRecords] = useState([]);

    const combinedSessions = useMemo(() => {
        const merged = [...sessionRecords, ...onboardingRecords];
        return merged.sort((a, b) => {
            const aTs = parseTimestampValue(a.timestamp);
            const bTs = parseTimestampValue(b.timestamp);
            if (aTs === null && bTs === null) {
                return 0;
            }
            if (aTs === null) {
                return 1;
            }
            if (bTs === null) {
                return -1;
            }
            return bTs - aTs;
        });
    }, [sessionRecords, onboardingRecords]);

    useEffect(() => {
        const cleanup = fetchDeviceSessions();
        return () => {
            if (cleanup) {
                cleanup();
            }
        };
    }, [company_key, device_id, setLoader]);

    const fetchDeviceSessions = () => {
        let sessionUnsubscribe;
        let onboardingUnsubscribe;
        setLoader(true);
        FirebaseApp().then(db => {
            const sessionRef = ref(db, `UsedCompanyKeys/${company_key}/${device_id}/timestamp`);
            sessionUnsubscribe = onValue(sessionRef, (snapshot) => {
                const records = [];
                snapshot.forEach(childSnapshot => {
                    records.push({
                        "timestamp": childSnapshot.key,
                        "session": childSnapshot.val(),
                        "type": "Session"
                    });
                });
                setSessionRecords(records);
                setLoader(false);
            }, (err) => {
                console.log('session listener error --->', err);
                setLoader(false);
            });

            const onboardingRef = ref(db, `MindResetDb/OnboardingInformation/${device_id}/onboarding`);
            onboardingUnsubscribe = onValue(onboardingRef, (snapshot) => {
                const records = [];
                const onboardingValue = snapshot.val();
                if (onboardingValue) {
                    const programName = onboardingValue.programName || onboardingValue.program_name || "Onboarding";
                    records.push({
                        "timestamp": onboardingValue.updated_at || onboardingValue.updatedAt || onboardingValue.count || null,
                        "session": `${programName}`,
                        "type": "Onboarding"
                    });
                }
                setOnboardingRecords(records);
                setLoader(false);
            }, (err) => {
                console.log('onboarding listener error --->', err);
                setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            setLoader(false);
        });

        return () => {
            if (sessionUnsubscribe) {
                sessionUnsubscribe();
            }
            if (onboardingUnsubscribe) {
                onboardingUnsubscribe();
            }
        };
    };

    const exportToCSV = () => {
        const csvHeader = 'Type,Session,Timestamp\n';
        const csvData = combinedSessions.map(d =>
            `${escapeCsvValue(d.type || 'Session')},${escapeCsvValue(stringifyValue(d.session))},${escapeCsvValue(formatTimestampDisplay(d.timestamp))}`
        ).join('\n');
        const csvContent = csvHeader + csvData;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${device_id}_sessions.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h1>
                            <button
                                type="button"
                                className="back-link-icon"
                                aria-label="Go back"
                                onClick={() => navigate(-1)}
                            >
                                <BsArrowLeft aria-hidden="true" />
                            </button>
                            <span className="text-orange">{device_id}</span> Sessions
                        </h1>
                    </Col>
                    <Col className="text-end">
                        <Button className="export-button" onClick={exportToCSV}>
                            Export to CSV
                        </Button>
                    </Col>
                </Row>
            </div>
            <section className="section dashboard">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Session</th>
                            <th>Timestamp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {combinedSessions.length > 0 ?
                            combinedSessions.map((d) =>
                                <tr key={`${d.type}-${d.timestamp}`}>
                                    <td>{d.type}</td>
                                    <td>{stringifyValue(d.session)}</td>
                                    <td>{formatTimestampDisplay(d.timestamp)}</td>
                                </tr>
                            ) :
                            <tr>
                                <td colSpan={3}>
                                    <h5 className="text-center">No data</h5>
                                </td>
                            </tr>

                        }
                    </tbody>
                </Table>
            </section>
        </main >
    )
}
